import { create } from 'zustand';
import { createAuthOperations, SupersededAuthOperation } from './auth-operations';
import {
  ApiRequestError,
  createGuestSession,
  exchangeFirebaseToken,
  getAuthMe,
  regenerateUsername as requestUsername,
  revokeSession,
  signInDebugTestUser,
  type ApiUser,
  type ApiSession,
} from '@/lib/api';
import { consumeSessionRedirect, observeSessionChanges } from '@/lib/browser-session';
import { isFirebaseConfigured, signInWithGoogle, signOutFirebase } from '@/lib/firebase-auth';

export interface AuthState {
  user: ApiUser | null;
  session: ApiSession | null;
  loading: boolean;
  error: string | null;
  signOutWarning: string | null;
  revision: number;
  regenerateUsername: (revision: number, signal: AbortSignal) => Promise<ApiUser>;
  startSessionSync: () => () => void;
  /** Bootstrap auth on app start. */
  bootstrap: () => Promise<void>;
  /** Sign in with Google via Firebase popup (or the development API when Firebase is absent). */
  signInWithGoogle: () => Promise<void>;
  /** Sign in with a development test member account. */
  signInAsTestMember: (displayName?: string) => Promise<void>;
  /** Sign out and create a new guest session. */
  signOut: () => Promise<void>;
}

function createLocalFallbackGuest(): ApiUser {
  const now = new Date().toISOString();
  return {
    id: 'usr_local_guest',
    email: null,
    displayName: 'Guest',
    customUsername: 'LocalGuest',
    avatarUrl: null,
    provider: 'guest',
    isAdmin: false,
    createdAt: now,
    lastLoginAt: now,
  };
}

export const useAuthStore = create<AuthState>((set, get) => {
  const operations = createAuthOperations(get);
  type AuthOperation = ReturnType<typeof operations.beginAuth>;
  let bootstrapAttempt: { operation: AuthOperation; promise: Promise<void> } | undefined;
  const publish = (identity: Pick<AuthState, 'user' | 'session' | 'loading' | 'error'>) =>
    set((state) => ({ ...identity, revision: state.revision + 1 }));
  const unavailable = (failure: unknown) => {
    const message = failure instanceof Error ? failure.message : 'Authentication is unavailable.';
    console.error('Authentication unavailable:', failure);
    publish({ user: createLocalFallbackGuest(), session: null, loading: false, error: message });
  };
  const acceptIdentity = (operation: AuthOperation, result: Awaited<ReturnType<typeof createGuestSession>>) => {
    operation.setToken(result.session.token);
    publish({ user: result.user, session: result.session, loading: false, error: null });
  };
  const restoreOrCreateGuest = async (operation: AuthOperation) => {
    if (operation.token) {
      try {
        return await getAuthMe(operation.token);
      } catch (error) {
        if (!(error instanceof ApiRequestError) || error.status !== 401) throw error;
        operation.setToken(null);
      }
    }
    return createGuestSession();
  };
  const reconcileStorage = (operation: AuthOperation) => {
    try {
      if (operation.isLatest() && operations.storageChanged()) void get().bootstrap();
    } catch (error) {
      if (operation.isLatest()) unavailable(error);
    }
  };
  const authenticate = async (request: () => ReturnType<typeof createGuestSession>) => {
    const operation = operations.beginAuth();
    try {
      await operation.run(request, (result) => acceptIdentity(operation, result));
    } catch (error) {
      if (operation.isLatest() && (operations.storageChanged() || get().loading)) void get().bootstrap();
      throw error;
    }
  };
  return {
    user: null,
    session: null,
    loading: true,
    error: null,
    signOutWarning: null,
    revision: 0,

    async regenerateUsername(revision, signal) {
      const operation = operations.beginProfile(revision, signal);
      return operation.run(
        () => requestUsername(operation.token, operation.user.id, operation.signal),
        (updated) => {
          if (updated.id !== operation.user.id) throw new Error('Profile response belongs to a different user.');
          if (!updated.customUsername) throw new Error('Profile response did not include a username.');
          const user = { ...operation.user, customUsername: updated.customUsername };
          set({ user });
          return user;
        },
      );
    },

    bootstrap() {
      try {
        consumeSessionRedirect();
        if (bootstrapAttempt?.operation.isCurrent()) return bootstrapAttempt.promise;
        const operation = operations.beginAuth();
        publish({ user: null, session: null, loading: true, error: null });
        const promise = operation
          .run(
            () => restoreOrCreateGuest(operation),
            (result) => acceptIdentity(operation, result),
          )
          .catch((error) => {
            if (operation.isLatest() && !(error instanceof SupersededAuthOperation)) unavailable(error);
          })
          .finally(() => {
            if (bootstrapAttempt?.operation === operation) bootstrapAttempt = undefined;
            reconcileStorage(operation);
          });
        bootstrapAttempt = { operation, promise };
        return promise;
      } catch (error) {
        operations.invalidate();
        unavailable(error);
        return Promise.resolve();
      }
    },

    async signInWithGoogle() {
      if (!isFirebaseConfigured()) {
        if (!import.meta.env.DEV) throw new Error('Google sign-in is not configured.');
        await get().signInAsTestMember();
        return;
      }
      await authenticate(async () => {
        const { idToken } = await signInWithGoogle();
        return exchangeFirebaseToken(idToken);
      });
    },

    async signInAsTestMember(displayName?: string) {
      if (!import.meta.env.DEV) throw new Error('Development sign-in is unavailable.');
      await authenticate(() => signInDebugTestUser(displayName ? { displayName } : undefined));
    },

    async signOut() {
      let operation: AuthOperation;
      try {
        operation = operations.beginAuth();
      } catch (error) {
        unavailable(error);
        return;
      }
      const token = operation.token;
      try {
        operation.setToken(null);
        set({ signOutWarning: null });
        publish({ user: null, session: null, loading: true, error: null });
        await operation.run(
          async () => {
            const [revocation, provider] = await Promise.allSettled([
              token ? revokeSession(token) : Promise.resolve(),
              signOutFirebase(),
            ]);
            const messages: string[] = [];
            if (revocation.status === 'rejected') {
              console.error('Session revocation failed:', revocation.reason);
              messages.push('Server sign-out could not be confirmed. The previous session may still be valid.');
            }
            if (provider.status === 'rejected') {
              console.error('Provider sign-out failed:', provider.reason);
              messages.push('Provider sign-out could not be confirmed.');
            }
            // Revocation uncertainty remains relevant even if another tab or sign-in replaced the identity.
            if (messages.length) set({ signOutWarning: messages.join(' ') });
          },
          () => undefined,
        );
        await operation.run(createGuestSession, (result) => acceptIdentity(operation, result));
      } catch (error) {
        if (operation.isLatest() && !(error instanceof SupersededAuthOperation)) unavailable(error);
      } finally {
        reconcileStorage(operation);
      }
    },

    startSessionSync() {
      const stop = observeSessionChanges(() => {
        try {
          if (operations.storageChanged() || get().error) void get().bootstrap();
        } catch {
          void get().bootstrap();
        }
      });
      void get().bootstrap();
      return stop;
    },
  };
});
